import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import DrawerShell from '../components/DrawerShell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ClipboardList, Search, LogOut, Loader2, AlertTriangle, Flame, Clock,
  CheckCircle2, XCircle, RefreshCw, FileText, ShoppingCart, Building2,
  Package, Layers, ArrowRight, Trash2,
} from 'lucide-react';
import axios from 'axios';
import { formatDateIST } from '../utils/date';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HOSPITAL_ADDR = 'Porunai Hospitals, Tenkasi - 627803';

const PRIORITY_CFG = {
  urgent: { label: 'Urgent', cls: 'bg-red-100 text-red-700 border-red-200 animate-pulse', rank: 0, icon: Flame },
  high: { label: 'High', cls: 'bg-orange-100 text-orange-700 border-orange-200', rank: 1, icon: AlertTriangle },
  normal: { label: 'Normal', cls: 'bg-blue-50 text-blue-700 border-blue-200', rank: 2, icon: Package },
  low: { label: 'Low', cls: 'bg-slate-100 text-slate-600 border-slate-200', rank: 3, icon: Clock },
};

const STATUS_CFG = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  converted: { label: 'Converted', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-500 border-slate-200', icon: XCircle },
};

export default function StoresAdminPage() {
  const { user, setUser } = useAuth();
  const [prs, setPrs] = useState([]);
  const [stats, setStats] = useState({ pending: 0, converted: 0, cancelled: 0, urgent_pending: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [showCreatePO, setShowCreatePO] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      const [prRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/stores/purchase-requests`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/stores/stats`, { withCredentials: true }),
      ]);
      setPrs(prRes.data || []);
      setStats(statsRes.data || stats);
      setSelected(new Set()); // Reset selection when list refreshes
    } catch (e) {
      toast.error('Failed to load purchase requests');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, priorityFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleLogout = async () => {
    try { await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch (_) {}
    setUser(null);
    window.location.assign('/login');
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return prs;
    const q = search.trim().toLowerCase();
    return prs.filter((r) =>
      (r.medicine_name || '').toLowerCase().includes(q)
      || (r.composition || '').toLowerCase().includes(q)
      || (r.raised_by_name || '').toLowerCase().includes(q)
    );
  }, [prs, search]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllPending = () => {
    const pending = filtered.filter((r) => r.status === 'pending').map((r) => r.id);
    if (selected.size === pending.length) setSelected(new Set());
    else setSelected(new Set(pending));
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this purchase request?')) return;
    try {
      await axios.patch(`${API_URL}/api/stores/purchase-requests/${id}`, {
        status: 'cancelled',
      }, { withCredentials: true });
      toast.success('PR cancelled');
      loadAll();
    } catch (e) {
      toast.error('Failed to cancel');
    }
  };

  const selectedPRs = filtered.filter((r) => selected.has(r.id) && r.status === 'pending');

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-purple-800 via-violet-900 to-fuchsia-900 text-white shadow-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Manrope' }}>
                Stores Admin
              </h1>
              <p className="text-xs text-white/70">Review PRs · Convert to Purchase Orders · ECare 360</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium" data-testid="stores-admin-username">{user?.name || 'Admin'}</p>
              <p className="text-[10px] text-white/60 uppercase tracking-wider">Stores Admin</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10 hover:text-white gap-1"
              onClick={handleLogout}
              data-testid="stores-admin-logout"
            >
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="sa-stats">
          <StatTile label="Pending" value={stats.pending} icon={Clock} accent="amber" />
          <StatTile label="Urgent / High" value={stats.urgent_pending} icon={Flame} accent="red" />
          <StatTile label="Converted to PO" value={stats.converted} icon={CheckCircle2} accent="emerald" />
          <StatTile label="Cancelled" value={stats.cancelled} icon={XCircle} accent="slate" />
        </div>

        <Card className="mb-4 border-slate-200 shadow-sm">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search medicine, composition, or requester..."
                className="pl-9 h-10"
                data-testid="sa-search"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {['pending', 'converted', 'cancelled', 'all'].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  data-testid={`sa-filter-${s}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                    filter === s
                      ? 'bg-purple-700 text-white border-purple-700'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {s === 'all' ? 'All' : (STATUS_CFG[s]?.label || s)}
                </button>
              ))}
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="h-8 text-xs w-[130px]" data-testid="sa-priority-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={loadAll} className="gap-1.5" data-testid="sa-refresh">
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Floating selection action bar */}
        {selectedPRs.length > 0 && (
          <div
            className="sticky top-[72px] z-20 mb-3 bg-gradient-to-r from-purple-700 to-fuchsia-700 text-white rounded-lg shadow-lg px-5 py-3 flex items-center justify-between"
            data-testid="sa-action-bar"
          >
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5" />
              <span className="text-sm font-bold">{selectedPRs.length} PR{selectedPRs.length > 1 ? 's' : ''} selected</span>
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs underline opacity-80 hover:opacity-100"
                data-testid="sa-clear-selection"
              >
                Clear
              </button>
            </div>
            <Button
              size="sm"
              onClick={() => setShowCreatePO(true)}
              className="bg-white text-purple-700 hover:bg-white/90 gap-1.5"
              data-testid="sa-generate-po"
            >
              <ShoppingCart className="h-4 w-4" /> Generate Purchase Order
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        )}

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {filter === 'pending' && filtered.some((r) => r.status === 'pending') && (
              <div className="px-5 py-2 border-b bg-slate-50 flex items-center gap-3">
                <Checkbox
                  checked={selected.size > 0 && selected.size === filtered.filter((r) => r.status === 'pending').length}
                  onCheckedChange={selectAllPending}
                  data-testid="sa-select-all"
                />
                <span className="text-xs font-semibold text-slate-600">Select all pending</span>
              </div>
            )}
            {loading && prs.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <Loader2 className="h-7 w-7 animate-spin mx-auto mb-3" />
                Loading requests…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-400" data-testid="sa-empty-state">
                <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm font-medium text-slate-500">No purchase requests in this view.</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[55vh]">
                <div className="divide-y divide-slate-100" data-testid="sa-list">
                  {filtered.map((r) => (
                    <PRReviewRow
                      key={r.id}
                      pr={r}
                      checked={selected.has(r.id)}
                      onToggle={() => toggleSelect(r.id)}
                      onCancel={() => handleCancel(r.id)}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </main>

      <CreatePODialog
        open={showCreatePO}
        onOpenChange={setShowCreatePO}
        selectedPRs={selectedPRs}
        onSuccess={loadAll}
      />
    </div>
  );
}

function StatTile({ label, value, icon: Icon, accent }) {
  const tones = {
    amber: 'bg-amber-50 border-amber-100 text-amber-800',
    red: 'bg-red-50 border-red-100 text-red-800',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <Card className={`border ${tones[accent] || tones.slate}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-white/70 flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
          <p className="text-2xl font-bold leading-none">{value ?? 0}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function PRReviewRow({ pr, checked, onToggle, onCancel }) {
  const PCfg = PRIORITY_CFG[pr.priority] || PRIORITY_CFG.normal;
  const SCfg = STATUS_CFG[pr.status] || STATUS_CFG.pending;
  const PIcon = PCfg.icon;
  const SIcon = SCfg.icon;
  const isPending = pr.status === 'pending';
  return (
    <div
      className={`px-5 py-3 flex items-start gap-3 transition ${
        checked ? 'bg-purple-50/50' : 'hover:bg-slate-50'
      }`}
      data-testid={`sa-row-${pr.id}`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        disabled={!isPending}
        className="mt-1"
        data-testid={`sa-row-check-${pr.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-slate-900 truncate">{pr.medicine_name}</p>
          <Badge variant="outline" className={`text-[10px] gap-1 ${PCfg.cls}`}>
            <PIcon className="h-3 w-3" /> {PCfg.label}
          </Badge>
          <Badge variant="outline" className={`text-[10px] gap-1 ${SCfg.cls}`}>
            <SIcon className="h-3 w-3" /> {SCfg.label}
          </Badge>
          {pr.converted_po_number && (
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 font-mono">
              {pr.converted_po_number}
            </Badge>
          )}
        </div>
        {pr.composition && <p className="text-[11px] text-blue-700 truncate mt-0.5">{pr.composition}</p>}
        <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500 flex-wrap">
          <span><span className="text-slate-400">Qty:</span> <b className="text-slate-700">{pr.quantity} {pr.unit}</b></span>
          {pr.current_stock !== undefined && pr.current_stock !== null && (
            <span>· <span className="text-slate-400">Stock:</span> <b className={pr.current_stock <= 10 ? 'text-red-600' : 'text-slate-700'}>{pr.current_stock}</b></span>
          )}
          {pr.last_mrp > 0 && <span>· <span className="text-slate-400">Last MRP:</span> <b>₹{pr.last_mrp}</b></span>}
          {pr.raised_by_name && <span>· <span className="text-slate-400">By</span> {pr.raised_by_name}</span>}
          {pr.created_at && <span>· {formatDateIST(pr.created_at)}</span>}
        </div>
        {pr.reason && (
          <p className="text-[11px] italic text-slate-500 mt-1 line-clamp-2">"{pr.reason}"</p>
        )}
      </div>
      {isPending && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          className="text-red-600 hover:bg-red-50 hover:text-red-700 h-8 px-2"
          data-testid={`sa-row-cancel-${pr.id}`}
          title="Cancel this PR"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

// ---------------- Create PO Dialog ----------------
function CreatePODialog({ open, onOpenChange, selectedPRs, onSuccess }) {
  const [draftItems, setDraftItems] = useState([]);
  const [matchedIds, setMatchedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [pickedVendor, setPickedVendor] = useState(null);
  const [paymentTerms, setPaymentTerms] = useState('Net 30 Days');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');

  // Pull a server-aggregated draft (sums identical medicines across the PRs)
  useEffect(() => {
    if (!open || !selectedPRs.length) return;
    setLoading(true);
    setDraftItems([]); setMatchedIds([]);
    setPickedVendor(null); setVendorSearch(''); setNotes(''); setDeliveryDate('');
    (async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/stores/purchase-requests/build-po-draft`, {
          pr_ids: selectedPRs.map((p) => p.id),
        }, { withCredentials: true });
        setDraftItems(data.items || []);
        setMatchedIds(data.matched_pr_ids || []);
      } catch (e) {
        const d = e.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Failed to build PO draft');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    if (!vendorSearch || vendorSearch.length < 2) { setVendors([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/vendors`, {
          params: { search: vendorSearch }, withCredentials: true,
        });
        if (!cancelled) setVendors((data || []).slice(0, 10));
      } catch (_) {}
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [vendorSearch]);

  const updateItem = (i, field, value) => {
    setDraftItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };
  const removeItem = (i) => {
    setDraftItems((prev) => prev.filter((_, idx) => idx !== i));
  };

  const totals = useMemo(() => {
    let subtotal = 0, gst = 0;
    for (const it of draftItems) {
      const qty = Number(it.quantity) || 0;
      const rate = Number(it.unit_price) || 0;
      const itemTotal = qty * rate;
      subtotal += itemTotal;
      gst += itemTotal * ((Number(it.gst_percent) || 0) / 100);
    }
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      gst: Math.round(gst * 100) / 100,
      grand: Math.round((subtotal + gst) * 100) / 100,
    };
  }, [draftItems]);

  const handleSubmit = async () => {
    if (!pickedVendor) { toast.error('Please pick a vendor'); return; }
    if (draftItems.length === 0) { toast.error('No items to order'); return; }
    setSubmitting(true);
    try {
      const { data: poData } = await axios.post(`${API_URL}/api/purchase-orders`, {
        vendor: pickedVendor,
        items: draftItems.map((it) => ({
          medicine_name: it.medicine_name,
          composition: it.composition || '',
          unit: it.unit || 'Nos',
          quantity: Number(it.quantity) || 0,
          free_qty: Number(it.free_qty) || 0,
          unit_price: Number(it.unit_price) || 0,
          hsn_code: it.hsn_code || '3004',
          gst_percent: Number(it.gst_percent) || 0,
        })),
        delivery_address: HOSPITAL_ADDR,
        payment_terms: paymentTerms,
        delivery_date: deliveryDate,
        notes,
        status: 'draft',
        created_by: 'Stores Admin',
      }, { withCredentials: true });

      // Now mark the source PRs as converted
      await axios.post(`${API_URL}/api/stores/purchase-requests/mark-converted`, {
        pr_ids: matchedIds,
        po_number: poData.po_number,
        po_id: poData.id,
      }, { withCredentials: true });

      toast.success(`PO ${poData.po_number} created · ${matchedIds.length} PRs linked`);
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to create PO');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerShell
      open={open}
      onClose={() => onOpenChange(false)}
      eyebrow={`${matchedIds.length} PR${matchedIds.length > 1 ? 's' : ''} → 1 PO`}
      title="Generate Purchase Order"
      subtitle="Aggregate selected requisitions, pick vendor, confirm totals"
      accent="#7e22ce"
      icon={<ShoppingCart className="h-5 w-5" />}
      width="xl"
      testId="sa-create-po-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || loading || !pickedVendor || draftItems.length === 0}
            className="bg-purple-700 hover:bg-purple-800 gap-1.5"
            data-testid="sa-po-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Create Purchase Order
          </Button>
        </>
      }
    >
      <ScrollArea className="h-full">
        <div className="px-6 py-4 space-y-4">
          {loading ? (
            <div className="text-center py-10 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Aggregating selected PRs…
            </div>
          ) : (
            <>
              {/* Vendor picker */}
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                    <Building2 className="h-3.5 w-3.5" /> Vendor / Supplier
                  </Label>
                  {pickedVendor ? (
                    <div className="flex items-center justify-between bg-purple-50 border border-purple-100 rounded p-3" data-testid="sa-po-vendor-picked">
                      <div>
                        <p className="text-sm font-bold text-slate-900">{pickedVendor.name}</p>
                        <p className="text-[10px] text-slate-500">
                          {pickedVendor.code} · {pickedVendor.gstin || 'No GSTIN'}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setPickedVendor(null)}>Change</Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        value={vendorSearch}
                        onChange={(e) => setVendorSearch(e.target.value)}
                          placeholder="Search vendor by name..."
                          className="pl-9 h-10"
                          data-testid="sa-po-vendor-search"
                        />
                        {vendors.length > 0 && (
                          <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg">
                            {vendors.map((v, i) => (
                              <button
                                key={i}
                                onClick={() => { setPickedVendor(v); setVendorSearch(''); setVendors([]); }}
                                className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm border-b last:border-b-0"
                                data-testid={`sa-po-vendor-result-${i}`}
                              >
                                <p className="font-semibold text-slate-900 truncate">{v.name}</p>
                                <p className="text-[10px] text-slate-500">{v.code} · {v.gstin || 'No GSTIN'}</p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Items table */}
                <Card className="border-slate-200">
                  <CardContent className="p-0">
                    <div className="px-4 py-3 border-b bg-slate-50">
                      <p className="text-xs font-semibold text-slate-600">
                        Line items <span className="text-slate-400">— review qty, rate & GST before submitting</span>
                      </p>
                    </div>
                    <div className="divide-y divide-slate-100" data-testid="sa-po-items">
                      {draftItems.length === 0 && (
                        <p className="text-center py-6 text-slate-400 text-sm">No items.</p>
                      )}
                      {draftItems.map((it, i) => {
                        const PCfg = PRIORITY_CFG[it.priority] || PRIORITY_CFG.normal;
                        return (
                          <div key={i} className="px-4 py-3" data-testid={`sa-po-item-${i}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900">{it.medicine_name}</p>
                                {it.composition && <p className="text-[10px] text-blue-700">{it.composition}</p>}
                                <p className="text-[10px] text-slate-400">
                                  Aggregated from {(it.source_pr_ids || []).length} PR{(it.source_pr_ids || []).length > 1 ? 's' : ''}
                                </p>
                              </div>
                              <Badge variant="outline" className={`text-[10px] ${PCfg.cls}`}>{PCfg.label}</Badge>
                              <Button size="sm" variant="ghost" onClick={() => removeItem(i)} className="text-red-600 h-7 w-7 p-0">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-5 gap-2">
                              <div>
                                <Label className="text-[10px] text-slate-500 mb-0.5 block">Qty</Label>
                                <Input
                                  type="number" min="0"
                                  value={it.quantity}
                                  onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                                  className="h-8 text-sm"
                                  data-testid={`sa-po-item-qty-${i}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-emerald-700 mb-0.5 block">Free</Label>
                                <Input
                                  type="number" min="0"
                                  value={it.free_qty || 0}
                                  onChange={(e) => updateItem(i, 'free_qty', e.target.value)}
                                  className="h-8 text-sm border-emerald-200 bg-emerald-50/30"
                                  data-testid={`sa-po-item-free-${i}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-slate-500 mb-0.5 block">UOM</Label>
                                <Input
                                  value={it.unit}
                                  onChange={(e) => updateItem(i, 'unit', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-slate-500 mb-0.5 block">Rate (₹)</Label>
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={it.unit_price}
                                  onChange={(e) => updateItem(i, 'unit_price', e.target.value)}
                                  className="h-8 text-sm"
                                  data-testid={`sa-po-item-rate-${i}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-slate-500 mb-0.5 block">GST %</Label>
                                <Input
                                  type="number" min="0" max="28"
                                  value={it.gst_percent}
                                  onChange={(e) => updateItem(i, 'gst_percent', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Totals + Terms */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card className="border-slate-200">
                    <CardContent className="p-4 space-y-2">
                      <div>
                        <Label className="text-xs font-semibold text-slate-700 mb-1 block">Payment Terms</Label>
                        <Input
                          value={paymentTerms}
                          onChange={(e) => setPaymentTerms(e.target.value)}
                          className="h-9 text-sm"
                          data-testid="sa-po-payment-terms"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-700 mb-1 block">Expected Delivery</Label>
                        <Input
                          type="date"
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                          className="h-9 text-sm"
                          data-testid="sa-po-delivery-date"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-700 mb-1 block">Notes (optional)</Label>
                        <Input
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Any special instructions..."
                          className="h-9 text-sm"
                          data-testid="sa-po-notes"
                        />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200 bg-purple-50/30">
                    <CardContent className="p-4 space-y-1.5" data-testid="sa-po-totals">
                      <div className="flex justify-between text-sm text-slate-700">
                        <span>Subtotal (taxable)</span><b>₹{totals.subtotal.toFixed(2)}</b>
                      </div>
                      <div className="flex justify-between text-sm text-slate-700">
                        <span>Total GST</span><b>₹{totals.gst.toFixed(2)}</b>
                      </div>
                      <div className="border-t pt-2 mt-1 flex justify-between text-base font-bold text-purple-900">
                        <span>Grand Total</span><span>₹{totals.grand.toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>
              </div>
            </>
          )}
        </div>
      </ScrollArea>
    </DrawerShell>
  );
}
